/* eslint-disable @typescript-eslint/no-explicit-any */
import {FC, useContext, useMemo} from 'react';
import Modal from 'components/Modal/Modal';
import GqlFormController from 'controllers/GqlFormController/GqlFormController';
import {Button, ModalProps} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {DocumentNode, OperationVariables} from '@apollo/client';
import {CANCEL_APPROVED_LEAVE_REQUEST} from 'graphql/absences/Mutations';
import {authContext} from 'contextApi/context/auth';
import {alertContext} from 'contextApi/context/error';
import {GET_CATALOGS} from 'graphql/absences/Querys';
import {Catalog} from 'models/catalog';
import GqlQueryController from 'controllers/GqlQueryController/GqlQueryController';
import {LeaveRequest} from 'models/leaveRequest';
import {useLogger} from 'hooks/useLogger';
import {useTranslateCatalog} from 'commons/hooks/useTranslateCatalog';
import './LeaveRequestApprovedCancellationModal.scss';

interface LeaveRequestApprovedCancellationModalProps
  extends Omit<ModalProps, 'children' | 'onSubmit'> {
  leaveRequestId: string | undefined;
  leaveData: LeaveRequest;
  children?: Children;
  mutation?: DocumentNode;
  variablesMapper?: (values: any) => OperationVariables;
}

const LeaveRequestApprovedCancellationModal: FC<
  LeaveRequestApprovedCancellationModalProps
> = ({
  onClose,
  leaveRequestId,
  leaveData,
  mutation = CANCEL_APPROVED_LEAVE_REQUEST,
  variablesMapper,
  ...other
}) => {
  const logger = useLogger(LeaveRequestApprovedCancellationModal.name);
  const {userDetails} = useContext(authContext);
  const {dispatch} = useContext(alertContext);
  const {t} = useTranslation();
  const handleOnSubmit = (mutationPromise: any, data: any) => {
    logger.debug('on submit', data);
    mutationPromise
      .then(() => {
        window.location.reload();
      })
      .catch((error: any) => {
        dispatch({
          type: 'show',
          payload: {level: 'error', message: error.message},
        });
      });
  };
  return (
    <GqlQueryController
      QUERY={GET_CATALOGS}
      variables={{type: 'CHANGE_REASON', country: userDetails?.data?.country}}
      initialState={[] as Catalog[]}
      rootField="catalogs"
    >
      {({data: cancellationTypes}) => (
        <ApprovedCancellationFormContent
          cancellationTypes={cancellationTypes}
          leaveRequestId={leaveRequestId}
          leaveData={leaveData}
          onClose={onClose}
          handleOnSubmit={handleOnSubmit}
          mutation={mutation}
          variablesMapper={variablesMapper}
          t={t}
          userDetails={userDetails}
          modalProps={other}
        />
      )}
    </GqlQueryController>
  );
};

// Inner component to handle catalog translation
const ApprovedCancellationFormContent: FC<any> = ({
  cancellationTypes,
  leaveRequestId,
  leaveData,
  onClose,
  handleOnSubmit,
  mutation,
  variablesMapper,
  t,
  userDetails,
  modalProps,
}) => {
  const {translateCatalogs} = useTranslateCatalog();
  const translatedCancellationTypes = useMemo(
    () => translateCatalogs(cancellationTypes),
    [cancellationTypes, translateCatalogs]
  );

  return (
    <Modal
      title={t('leaves.LeaveRequestApprovedCancellationModal.title')}
      description={t(
        'leaves.LeaveRequestApprovedCancellationModal.description'
      )}
      onClose={onClose}
      {...modalProps}
    >
      <GqlFormController
        mutation={mutation}
        variablesMapper={
          variablesMapper ??
          ((values: any) => ({
            cancelApprovalRequest: {
              cancelReason: Number(values.cancellation_reason),
              canceledUserId: userDetails?.data?.id,
              cancellationDetails: values.cancellation_details,
              id: leaveRequestId,
            },
          }))
        }
        onSubmit={handleOnSubmit}
        className="flex flex-col"
        itemProps={{xs: 12}}
        controls={[
          {
            name: 'typeOfLeave',
            type: 'label',
            props: {
              label: t('leaves.LeaveRequestUpdateModal.typeOfLeave'),
              value: leaveData?.typeOfLeave?.description,
            },
          },
          {
            name: 'cancellation_reason',
            type: 'select',
            options: {
              required: true,
            },
            props: {
              label: t(
                'leaves.LeaveRequestApprovedCancellationModal.cancellation_reason_label'
              ),
              placeholder: t(
                'leaves.LeaveRequestApprovedCancellationModal.cancellation_reason_placeholder'
              ),
              className: 'mt-2',
              noSelection: t('common.choose_an_option'),
              children: (translatedCancellationTypes || []).map(
                (option: any) => ({
                  key: option.id,
                  description: option.description,
                })
              ),
            },
          },
          {
            name: 'cancellation_details',
            props: {
              label: t(
                'leaves.LeaveRequestApprovedCancellationModal.cancellation_details_label'
              ),
              className: 'mt-2',
              multiline: true,
              maxRows: 4,
            },
          },
        ]}
      >
        {({onSubmit}: any) => (
          <div>
            <Button
              variant="outlined"
              onClick={event => onClose && onClose(event, 'backdropClick')}
            >
              {t('leaves.LeaveRequestApprovedCancellationModal.exit')}
            </Button>
            <Button onClick={onSubmit()} variant="contained">
              {t('leaves.LeaveRequestApprovedCancellationModal.submit')}
            </Button>
          </div>
        )}
      </GqlFormController>
    </Modal>
  );
};

export default LeaveRequestApprovedCancellationModal;
